import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import Modal from './Modal';
import TrainerConfigForm, { isTrainerConfigReady, useTrainerKindMeta } from './TrainerConfigForm';
import { nextChoiceOptionId } from '../utils/workAttemptHelpers';

const EMPTY_OPTIONS = [
  { id: 'a', text: '' },
  { id: 'b', text: '' },
];

function blankPromptForm() {
  return {
    prompt: '',
    kind: 'single_choice',
    options: EMPTY_OPTIONS.map((o) => ({ ...o })),
    expected_answer: 'a',
    save_to_bank: true,
  };
}

/**
 * Блок «Быстрые задания» на вкладке журнала у преподавателя.
 * activeSessionId — id идущего урока (null, если урока нет).
 */
export default function LessonQuickItemsPanel({
  activeSessionId = null,
  studentId = null,
  relationActive = true,
  onChanged = null,
}) {
  const [items, setItems] = useState([]);
  const [bank, setBank] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [promptOpen, setPromptOpen] = useState(false);
  const [promptForm, setPromptForm] = useState(blankPromptForm);
  const [bankOpen, setBankOpen] = useState(false);

  const [trainerOpen, setTrainerOpen] = useState(false);
  const [trainerKinds, setTrainerKinds] = useState([]);
  const [trainerKind, setTrainerKind] = useState('');
  const [trainerTitle, setTrainerTitle] = useState('');
  const { meta: trainerMeta, loading: trainerMetaLoading } = useTrainerKindMeta(trainerKind || null);
  const [trainerConfig, setTrainerConfig] = useState({});

  const loadItems = useCallback(async () => {
    if (!activeSessionId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/teacher/lesson-sessions/${activeSessionId}/quick-items/`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError('Не удалось загрузить быстрые задания.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [activeSessionId]);

  const loadBank = useCallback(async () => {
    try {
      const res = await api.get('/teacher/quick-prompts/');
      setBank(Array.isArray(res.data) ? res.data : []);
    } catch {
      setBank([]);
    }
  }, []);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  useEffect(() => {
    void loadBank();
  }, [loadBank]);

  const implementedKinds = useMemo(
    () => (trainerKinds || []).filter((k) => k.implemented),
    [trainerKinds],
  );

  async function ensureTrainerKinds() {
    if (trainerKinds.length) return;
    try {
      const res = await api.get('/trainers/catalog/');
      setTrainerKinds(Array.isArray(res.data) ? res.data : []);
    } catch {
      setTrainerKinds([]);
    }
  }

  function bump() {
    if (typeof onChanged === 'function') onChanged();
  }

  async function attachFromBank(promptId) {
    if (!activeSessionId || busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.post(`/teacher/lesson-sessions/${activeSessionId}/quick-items/`, {
        prompt_id: promptId,
      });
      setNotice('Вопрос добавлен на урок.');
      setBankOpen(false);
      await loadItems();
      bump();
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось добавить вопрос.');
    } finally {
      setBusy(false);
    }
  }

  async function submitPrompt(e) {
    e?.preventDefault?.();
    if (!activeSessionId || busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    const payload = {
      prompt: promptForm.prompt,
      kind: promptForm.kind,
      expected_answer: promptForm.expected_answer,
      save_to_bank: promptForm.save_to_bank,
    };
    if (promptForm.kind === 'single_choice') {
      payload.options = promptForm.options
        .map((o) => ({ id: String(o.id || '').trim(), text: String(o.text || '').trim() }))
        .filter((o) => o.id && o.text);
    } else {
      payload.options = [];
    }
    try {
      await api.post(`/teacher/lesson-sessions/${activeSessionId}/quick-items/`, payload);
      setNotice('Вопрос поставлен на урок.');
      setPromptOpen(false);
      setPromptForm(blankPromptForm());
      await loadItems();
      await loadBank();
      bump();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось поставить вопрос.');
    } finally {
      setBusy(false);
    }
  }

  async function submitTrainer(e) {
    e?.preventDefault?.();
    if (!activeSessionId || busy || !trainerKind) return;
    if (trainerMetaLoading || !isTrainerConfigReady(trainerMeta, trainerConfig)) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.post(`/teacher/lesson-sessions/${activeSessionId}/quick-items/`, {
        trainer: {
          kind: trainerKind,
          title: trainerTitle.trim(),
          config: trainerConfig,
          target_count: trainerConfig.target_count || 10,
        },
      });
      setNotice('Тренажёр прикреплён (не как ДЗ).');
      setTrainerOpen(false);
      setTrainerKind('');
      setTrainerTitle('');
      setTrainerConfig({});
      await loadItems();
      bump();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось прикрепить тренажёр.');
    } finally {
      setBusy(false);
    }
  }

  async function removeItem(item) {
    if (!activeSessionId || busy || !item?.id) return;
    const label = item.kind === 'trainer' ? (item.title || 'тренажёр') : 'вопрос';
    if (!window.confirm(`Снять с урока: ${label}?`)) return;
    setBusy(true);
    setError('');
    try {
      await api.delete(`/teacher/lesson-sessions/${activeSessionId}/quick-items/${item.id}/`);
      await loadItems();
      bump();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось снять.');
    } finally {
      setBusy(false);
    }
  }

  async function deleteBankPrompt(qp) {
    if (!qp?.id || busy) return;
    if (!window.confirm('Удалить вопрос из копилки? На уже поставленных уроках снимок останется.')) return;
    setBusy(true);
    try {
      await api.delete(`/teacher/quick-prompts/${qp.id}/`);
      await loadBank();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось удалить из копилки.');
    } finally {
      setBusy(false);
    }
  }

  function updateOption(idx, field, value) {
    setPromptForm((prev) => {
      const options = prev.options.map((o, i) => (i === idx ? { ...o, [field]: value } : o));
      return { ...prev, options };
    });
  }

  function addOption() {
    setPromptForm((prev) => {
      const id = nextChoiceOptionId(prev.options.map((o) => o.id));
      return { ...prev, options: [...prev.options, { id, text: '' }] };
    });
  }

  function removeOption(idx) {
    setPromptForm((prev) => {
      if (prev.options.length <= 2) return prev;
      const options = prev.options.filter((_, i) => i !== idx);
      let expected = prev.expected_answer;
      if (!options.some((o) => o.id === expected)) {
        expected = options[0]?.id || '';
      }
      return { ...prev, options, expected_answer: expected };
    });
  }

  const disabled = !relationActive || !activeSessionId || busy;

  return (
    <div className="agenda-block lesson-quick-panel">
      <h4>Быстрые задания</h4>
      <p className="text-secondary" style={{ fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
        Короткие вопросы и тренажёры на паузу, пока вы заняты с другим учеником.
        Не попадают в ДЗ. Ученик отвечает в окне поверх текущей задачи.
      </p>
      {!activeSessionId ? (
        <p className="text-secondary" style={{ fontSize: '0.85rem' }}>
          Начните урок, чтобы ставить быстрые задания на эту карточку.
        </p>
      ) : null}
      {notice ? <p className="alert alert-success assign-notice" role="status">{notice}</p> : null}
      {error ? <div className="alert alert-error">{error}</div> : null}
      <div className="assign-actions">
        <button
          type="button"
          className="btn btn-sm btn-outline"
          disabled={disabled}
          onClick={() => {
            setPromptForm(blankPromptForm());
            setPromptOpen(true);
          }}
        >
          Написать вопрос
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline"
          disabled={disabled}
          onClick={() => {
            void loadBank();
            setBankOpen(true);
          }}
        >
          Из копилки
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline"
          disabled={disabled}
          onClick={() => {
            void ensureTrainerKinds();
            setTrainerKind('');
            setTrainerTitle('');
            setTrainerConfig({});
            setTrainerOpen(true);
          }}
        >
          Прикрепить тренажёр
        </button>
      </div>

      {loading ? (
        <p className="text-secondary" style={{ marginTop: '0.75rem' }}>Загрузка…</p>
      ) : items.length > 0 ? (
        <ul className="agenda-list agenda-list-editable" style={{ marginTop: '0.75rem' }}>
          {items.map((item) => (
            <li key={item.id}>
              <span className="text-secondary" style={{ fontSize: '0.75rem', marginRight: '0.35rem' }}>
                {item.kind === 'trainer' ? 'Тренажёр' : 'Вопрос'}
              </span>
              {item.kind === 'trainer' ? (
                <Link
                  to={item.href || '/trainers'}
                  className="link-like"
                >
                  {item.title || 'Тренажёр'}
                </Link>
              ) : (
                <span>{item.prompt || 'Вопрос'}</span>
              )}
              {item.kind !== 'trainer' && item.answered ? (
                <span className="text-secondary" style={{ marginLeft: '0.35rem', fontSize: '0.85rem' }}>
                  · {item.is_correct ? 'верно' : 'неверно'}
                  {item.student_answer ? ` («${item.student_answer}»)` : ''}
                </span>
              ) : null}
              {item.kind === 'trainer' && item.has_started ? (
                <span className="text-secondary" style={{ marginLeft: '0.35rem', fontSize: '0.85rem' }}>
                  · начат
                </span>
              ) : null}
              <button
                type="button"
                className="btn btn-xs btn-outline agenda-remove"
                title="Снять с урока"
                disabled={busy}
                onClick={() => void removeItem(item)}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      ) : activeSessionId ? (
        <p className="text-secondary" style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
          Пока пусто.
        </p>
      ) : null}

      <Modal
        open={promptOpen}
        title="Быстрый вопрос"
        onClose={() => {
          if (busy) return;
          setPromptOpen(false);
        }}
      >
        <form onSubmit={(e) => void submitPrompt(e)}>
          <label className="form-label" style={{ display: 'block' }}>
            Текст вопроса
            <textarea
              className="form-input"
              rows={3}
              value={promptForm.prompt}
              onChange={(e) => setPromptForm((p) => ({ ...p, prompt: e.target.value }))}
              required
              style={{ width: '100%', resize: 'vertical' }}
            />
          </label>
          <label className="form-label" style={{ display: 'block', marginTop: '0.75rem' }}>
            Тип
            <select
              className="form-input"
              value={promptForm.kind}
              onChange={(e) => setPromptForm((p) => ({
                ...p,
                kind: e.target.value,
                expected_answer: e.target.value === 'single_choice'
                  ? (p.options[0]?.id || 'a')
                  : '',
              }))}
            >
              <option value="single_choice">Один вариант</option>
              <option value="text">Короткий текст</option>
            </select>
          </label>
          {promptForm.kind === 'single_choice' ? (
            <div style={{ marginTop: '0.75rem' }}>
              <div className="text-secondary" style={{ fontSize: '0.85rem', marginBottom: '0.35rem' }}>
                Варианты (отметьте верный)
              </div>
              {promptForm.options.map((opt, idx) => (
                <div key={opt.id} style={{ display: 'flex', gap: '0.35rem', marginBottom: '0.35rem', alignItems: 'center' }}>
                  <input
                    type="radio"
                    name="quick-expected"
                    checked={promptForm.expected_answer === opt.id}
                    onChange={() => setPromptForm((p) => ({ ...p, expected_answer: opt.id }))}
                    title="Верный"
                  />
                  <span className="text-secondary" style={{ minWidth: '1.2rem' }}>{opt.id}.</span>
                  <input
                    type="text"
                    className="form-input"
                    value={opt.text}
                    onChange={(e) => updateOption(idx, 'text', e.target.value)}
                    placeholder="Текст варианта"
                    required
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn btn-xs btn-outline"
                    disabled={promptForm.options.length <= 2}
                    onClick={() => removeOption(idx)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-xs btn-outline" onClick={addOption}>
                + вариант
              </button>
            </div>
          ) : (
            <label className="form-label" style={{ display: 'block', marginTop: '0.75rem' }}>
              Верный ответ
              <input
                type="text"
                className="form-input"
                value={promptForm.expected_answer}
                onChange={(e) => setPromptForm((p) => ({ ...p, expected_answer: e.target.value }))}
                required
              />
            </label>
          )}
          <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginTop: '0.75rem' }}>
            <input
              type="checkbox"
              checked={promptForm.save_to_bank}
              onChange={(e) => setPromptForm((p) => ({ ...p, save_to_bank: e.target.checked }))}
            />
            <span>Сохранить в копилку</span>
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
            <button type="submit" className="btn btn-primary" disabled={busy || !promptForm.prompt.trim()}>
              {busy ? '…' : 'Поставить на урок'}
            </button>
            <button type="button" className="btn btn-outline" disabled={busy} onClick={() => setPromptOpen(false)}>
              Отмена
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={bankOpen}
        title="Копилка вопросов"
        onClose={() => {
          if (busy) return;
          setBankOpen(false);
        }}
      >
        {bank.length === 0 ? (
          <p className="text-secondary">Пока пусто. Напишите вопрос и отметьте «Сохранить в копилку».</p>
        ) : (
          <ul className="agenda-list">
            {bank.map((qp) => (
              <li key={qp.id} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
                <span style={{ flex: '1 1 12rem' }}>{qp.prompt}</span>
                <span className="text-secondary" style={{ fontSize: '0.8rem' }}>
                  {qp.kind === 'text' ? 'текст' : 'выбор'}
                </span>
                <button
                  type="button"
                  className="btn btn-xs btn-primary"
                  disabled={busy || !activeSessionId}
                  onClick={() => void attachFromBank(qp.id)}
                >
                  На урок
                </button>
                <button
                  type="button"
                  className="btn btn-xs btn-outline"
                  disabled={busy}
                  title="Удалить из копилки"
                  onClick={() => void deleteBankPrompt(qp)}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      <Modal
        open={trainerOpen}
        title="Прикрепить тренажёр"
        onClose={() => {
          if (busy) return;
          setTrainerOpen(false);
        }}
      >
        <form onSubmit={(e) => void submitTrainer(e)}>
          <p className="text-secondary" style={{ marginBottom: '0.75rem' }}>
            Назначение без пункта ДЗ. Ученик откроет в новой вкладке.
          </p>
          {!studentId ? (
            <p className="text-secondary">Нет ученика.</p>
          ) : (
            <>
              <label className="form-label" style={{ display: 'block' }}>
                Тренажёр
                <select
                  className="form-input"
                  value={trainerKind}
                  onChange={(e) => {
                    const slug = e.target.value;
                    setTrainerKind(slug);
                    const found = implementedKinds.find((k) => k.slug === slug);
                    setTrainerTitle(found?.title || '');
                    setTrainerConfig({});
                  }}
                  required
                >
                  <option value="">Выберите…</option>
                  {implementedKinds.map((k) => (
                    <option key={k.slug} value={k.slug}>{k.title || k.slug}</option>
                  ))}
                </select>
              </label>
              {trainerKind ? (
                <>
                  <label className="form-label" style={{ display: 'block', marginTop: '0.75rem' }}>
                    Название (необязательно)
                    <input
                      type="text"
                      className="form-input"
                      value={trainerTitle}
                      onChange={(e) => setTrainerTitle(e.target.value)}
                      placeholder={trainerMeta?.title || ''}
                    />
                  </label>
                  <div style={{ marginTop: '0.75rem' }}>
                    {trainerMetaLoading ? (
                      <p className="text-secondary">Загрузка настроек…</p>
                    ) : (
                      <TrainerConfigForm
                        meta={trainerMeta}
                        config={trainerConfig}
                        onChange={setTrainerConfig}
                        disabled={busy}
                      />
                    )}
                  </div>
                </>
              ) : null}
            </>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                busy
                || !trainerKind
                || trainerMetaLoading
                || !isTrainerConfigReady(trainerMeta, trainerConfig)
              }
            >
              {busy ? '…' : 'Прикрепить'}
            </button>
            <button type="button" className="btn btn-outline" disabled={busy} onClick={() => setTrainerOpen(false)}>
              Отмена
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
